// @flow
import invariant from 'invariant';
import * as React from 'react';
import { Route, Switch } from 'react-router-dom';
import ErrorBoundary from 'containers/ErrorBoundary';
import AmpContext from 'utils/AmpContext';
import { UpdateRequiredContextProvider } from 'containers/UpdateRequiredContext';
import { TabContextProvider } from 'containers/TabContext';
import { SEARCH_LANDING_ROUTES } from 'utils/constants';
import AuthenticatedRoute from 'containers/AuthenticatedRoute';
import LandingPageContainerLoadable from 'pages/LandingPageContainerLoadable';
import LoginPageContainerLoadable from 'pages/LoginPageContainerLoadable';
import SignupPageContainerLoadable from 'pages/SignupPageContainerLoadable';
import ForgotPasswordPageContainerLoadable from 'pages/ForgotPasswordPageContainerLoadable';
import ResetPasswordPageContainerLoadable from 'pages/ResetPasswordPageContainerLoadable';
import InvalidLinkLoadable from 'pages/InvalidLinkLoadable';
import CompensationSurveyLoadable from 'pages/CompensationSurveyLoadable';
import SalaryReportLoadable from 'pages/SalaryReportLoadable';
import OrgPageContainerLoadable from 'pages/OrgPageContainerLoadable';
import ListingPageContainerLoadable from 'pages/ListingPageContainerLoadable';
import ImportedListingPageContainerLoadable from 'pages/ImportedListingPageContainerLoadable';
import GroupPageContainerLoadable from 'pages/GroupPageContainerLoadable';
import EventPageContainerLoadable from 'pages/EventPageContainerLoadable';
import UserInviteContainerLoadable from 'pages/UserInviteContainerLoadable';
import BuyCreditsLinksLoadable from 'pages/BuyCreditsLinksLoadable';
import CreditsLinksLoadable from 'pages/CreditsLinksLoadable';
import AdminsLinksLoadable from 'pages/AdminsLinksLoadable';
import EditOrgLinksLoadable from 'pages/EditOrgLinksLoadable';
import GradFairsLinksLoadable from 'pages/GradFairsLinksLoadable';
import InvoicesLinksLoadable from 'pages/InvoicesLinksLoadable';
import InvoiceViewContainerLoadable from 'pages/InvoiceViewContainerLoadable';
import NotificationsLinksLoadable from 'pages/NotificationsLinksLoadable';
import UserDashboardLoadable from 'pages/UserDashboardLoadable';
import OrgDashboardLoadable from 'pages/OrgDashboardLoadable';
import GroupDashboardLoadable from 'pages/GroupDashboardLoadable';
import GradProgramPageContainerLoadable from 'pages/gradSchools/GradProgramPageContainerLoadable';
import ShowUserModals from 'containers/ShowUserModals';
import SetFlashMessages from 'containers/SetFlashMessages';
import SetGMapsError from 'containers/SetGMapsError';
import ContentPageContainerLoadable from 'pages/ContentPageContainerLoadable';

const ORG_TYPE_URL_SEGMENTS = [
  // en
  'nonprofit',
  'corporation',
  'government',
  'consultant',
  'social-enterprise',
  'recruiter',
  'corporation',
  // es
  'ong',
  'gobierno',
  'consultor',
  'empresa-social',
  'agencia-de-contratacion',
  'empresa',
  // pt
  'ong',
  'governo',
  'consultoria',
  'empreendedorismo-social',
  'recrutador',
  'empresa'
];

const JOB_URL_SEGMENTS = [
  'job', // en
  'empleo', // es
  'emprego' // pt
];

const INTERNSHIP_URL_SEGMENTS = [
  'internship', // en
  'pasantia', // es
  'estagio' // pt
];

const VOLOP_URL_SEGMENTS = [
  'volunteer-opportunity', // en
  'volunter-opportunity', // en deprecated. TODO: remove in April 2021.
  'oportunidad-voluntariado', // es
  'oportunidade-voluntariado' // pt
];

const GROUP_URL_SEGMENTS = [
  'group', // en
  'grupo', // es
  'coletivo' // pt
];

const IDEALIST_DAY_EVENT_URL_SEGMENTS = [
  'idealist-day-event', // en
  'dia-idealist-evento', // es
  'encontro-dia-idealista' // pt
];

const GRAD_PROGRAM_URL_SEGMENTS = [
  'grad-program', // en
  'programa-posgrado', // es
  'programa-posgraduacao' // pt
];

const ORG_TYPES_RE = `(${ORG_TYPE_URL_SEGMENTS.join('|')})`;
const JOBS_RE = `(${JOB_URL_SEGMENTS.join('|')})`;
const INTERNSHIPS_RE = `(${INTERNSHIP_URL_SEGMENTS.join('|')})`;
const VOLOPS_RE = `(${VOLOP_URL_SEGMENTS.join('|')})`;
const GROUPS_RE = `(${GROUP_URL_SEGMENTS.join('|')})`;
const IDEALIST_DAY_EVENTS_RE = `(${IDEALIST_DAY_EVENT_URL_SEGMENTS.join('|')})`;
const GRAD_PROGRAMS_RE = `(${GRAD_PROGRAM_URL_SEGMENTS.join('|')})`;

const App = ({ ampEnabled }: { ampEnabled?: boolean }) => (
  <ErrorBoundary>
    <SetGMapsError />
    <SetFlashMessages />
    <ShowUserModals>
      <AmpContext.Provider value={!!ampEnabled}>
        <UpdateRequiredContextProvider>
          <TabContextProvider>
            <Switch>
              <Route
                exact
                path="/login"
                component={LoginPageContainerLoadable}
              />
              <Route
                exact
                path="/signup"
                component={SignupPageContainerLoadable}
              />
              <Route
                exact
                path="/forgot-password"
                component={ForgotPasswordPageContainerLoadable}
              />
              <Route
                exact
                path="/reset-password/:token"
                component={ResetPasswordPageContainerLoadable}
              />
              <Route
                exact
                path="/invalid-or-expired-link"
                component={InvalidLinkLoadable}
              />
              <Route
                exact
                path="/survey/start"
                component={CompensationSurveyLoadable}
              />
              <Route
                exact
                path="/survey/report"
                component={SalaryReportLoadable}
              />
              <Route
                exact
                path="/invoices/:invoiceId([0-9A-Za-z]{32})"
                render={({
                  match: {
                    params: { invoiceId }
                  }
                }) => {
                  invariant(invoiceId, 'Missing invoiceId');
                  return <InvoiceViewContainerLoadable invoiceId={invoiceId} />;
                }}
              />
              <Route
                exact
                path="/donation-receipts/:invoiceId([0-9A-Za-z]{32})"
                render={({
                  match: {
                    params: { invoiceId }
                  }
                }) => {
                  invariant(invoiceId, 'Missing invoiceId');
                  return (
                    <InvoiceViewContainerLoadable
                      invoiceId={invoiceId}
                      isDonation
                    />
                  );
                }}
              />
              <Route
                exact
                path={`/:lang/:amp(amp)?/${ORG_TYPES_RE}/:orgId-(.*)`}
                render={({
                  match: {
                    params: { orgId }
                  }
                }) => {
                  invariant(orgId, 'Missing orgId');
                  return <OrgPageContainerLoadable orgId={orgId} />;
                }}
              />
              <Route
                exact
                path={`/:lang/:amp(amp)?/${JOBS_RE}/:jobId-(.*)`}
                render={({
                  match: {
                    params: { jobId }
                  }
                }) => {
                  invariant(jobId, 'Missing jobId');
                  return (
                    <ListingPageContainerLoadable
                      listingType="JOB"
                      listingId={jobId}
                    />
                  );
                }}
              />
              <Route
                exact
                path={`/:lang/:amp(amp)?/${ORG_TYPES_RE}-${JOBS_RE}/:jobId-(.*)`}
                render={({
                  match: {
                    params: { jobId }
                  }
                }) => {
                  invariant(jobId, 'Missing jobId');
                  return (
                    <ListingPageContainerLoadable
                      listingType="JOB"
                      listingId={jobId}
                    />
                  );
                }}
              />
              <Route
                exact
                path={`/:lang/:amp(amp)?/${INTERNSHIPS_RE}/:internshipId-(.*)`}
                render={({
                  match: {
                    params: { internshipId }
                  }
                }) => {
                  invariant(internshipId, 'Missing internshipId');
                  return (
                    <ListingPageContainerLoadable
                      listingType="INTERNSHIP"
                      listingId={internshipId}
                    />
                  );
                }}
              />
              <Route
                exact
                path={`/:lang/:amp(amp)?/${ORG_TYPES_RE}-${INTERNSHIPS_RE}/:internshipId-(.*)`}
                render={({
                  match: {
                    params: { internshipId }
                  }
                }) => {
                  invariant(internshipId, 'Missing internshipId');
                  return (
                    <ListingPageContainerLoadable
                      listingType="INTERNSHIP"
                      listingId={internshipId}
                    />
                  );
                }}
              />
              <Route
                exact
                path={`/:lang/:amp(amp)?/${VOLOPS_RE}/:volopId-(.*)`}
                render={({
                  match: {
                    params: { volopId }
                  }
                }) => {
                  invariant(volopId, 'Missing volopId');
                  return (
                    <ListingPageContainerLoadable
                      listingType="VOLOP"
                      listingId={volopId}
                    />
                  );
                }}
              />
              <Route
                exact
                path="/:lang/imported-action-opps/:source/:volopId"
                render={({
                  match: {
                    params: { volopId, source }
                  }
                }) => {
                  invariant(volopId, 'Missing volopId');
                  return (
                    <ImportedListingPageContainerLoadable
                      source={source}
                      listingId={volopId}
                    />
                  );
                }}
              />
              <Route
                exact
                path={`/:lang/:amp(amp)?/${GROUPS_RE}/:groupId-(.*)`}
                render={({
                  match: {
                    params: { groupId }
                  }
                }) => {
                  invariant(groupId, 'Missing groupId');
                  return <GroupPageContainerLoadable groupId={groupId} />;
                }}
              />
              <Route
                exact
                path={`/:lang/:amp(amp)?/${IDEALIST_DAY_EVENTS_RE}/:eventId-(.*)`}
                render={({
                  match: {
                    params: { eventId }
                  }
                }) => {
                  invariant(eventId, 'Missing eventId');
                  return <EventPageContainerLoadable eventId={eventId} />;
                }}
              />
              <Route
                exact
                path="/user-invites/:inviteId"
                render={({
                  match: {
                    params: { inviteId }
                  }
                }) => {
                  invariant(inviteId, 'missing invite');
                  return <UserInviteContainerLoadable inviteId={inviteId} />;
                }}
              />
              <AuthenticatedRoute
                path="/dashboard"
                component={UserDashboardLoadable}
              />
              <AuthenticatedRoute
                path="/organizations/create"
                component={UserDashboardLoadable}
              />
              <AuthenticatedRoute
                path="/organizations/create-with-grad-program"
                component={UserDashboardLoadable}
              />
              <AuthenticatedRoute
                path="/admin/:orgId"
                render={({
                  match: {
                    params: { orgId }
                  },
                  location
                }) => {
                  invariant(orgId, 'Missing orgId');
                  return (
                    <OrgDashboardLoadable orgId={orgId} location={location} />
                  );
                }}
              />
              <AuthenticatedRoute
                path="/post-a-listing"
                component={UserDashboardLoadable}
              />
              <AuthenticatedRoute
                path="/select-org"
                component={UserDashboardLoadable}
              />
              <AuthenticatedRoute
                path="/post-idealist-day"
                component={UserDashboardLoadable}
              />
              <AuthenticatedRoute
                path="/manage-grad-program"
                component={UserDashboardLoadable}
              />
              <AuthenticatedRoute
                exact
                path="/buy-credits"
                component={BuyCreditsLinksLoadable}
              />
              <AuthenticatedRoute
                exact
                path="/notifications"
                component={NotificationsLinksLoadable}
              />
              <AuthenticatedRoute
                exact
                path="/credits"
                component={CreditsLinksLoadable}
              />
              <AuthenticatedRoute
                exact
                path="/admins"
                component={AdminsLinksLoadable}
              />
              <AuthenticatedRoute
                exact
                path="/edit-org"
                component={EditOrgLinksLoadable}
              />
              <AuthenticatedRoute
                exact
                path="/gradfairs"
                component={GradFairsLinksLoadable}
              />
              <AuthenticatedRoute
                exact
                path="/invoices"
                component={InvoicesLinksLoadable}
              />
              <AuthenticatedRoute
                path="/groups/create"
                component={UserDashboardLoadable}
              />
              <AuthenticatedRoute
                path="/group-admin/:groupId"
                render={({
                  match: {
                    params: { groupId }
                  },
                  location
                }) => {
                  invariant(groupId, 'Missing groupId');
                  return (
                    <GroupDashboardLoadable
                      groupId={groupId}
                      location={location}
                    />
                  );
                }}
              />
              <Route
                exact
                path={`/:lang/:amp(amp)?/${GRAD_PROGRAMS_RE}/:programId-(.*)`}
                render={({
                  match: {
                    params: { programId }
                  }
                }) => {
                  invariant(programId, 'Missing programId');
                  return (
                    <GradProgramPageContainerLoadable programId={programId} />
                  );
                }}
              />
              {SEARCH_LANDING_ROUTES.map(l => (
                <Route
                  key={l}
                  path={l}
                  component={LandingPageContainerLoadable}
                />
              ))}
              <Route
                path="/:amp(amp)?/:slug*"
                render={({
                  match: {
                    params: { slug }
                  }
                }) => <ContentPageContainerLoadable slug={slug} />}
              />
            </Switch>
          </TabContextProvider>
        </UpdateRequiredContextProvider>
      </AmpContext.Provider>
    </ShowUserModals>
  </ErrorBoundary>
);

export default App;
